// floorPlanItemPlacement.js

/**
 * Helpers for linking PURCHASE_ORDER_ITEM rows to floor-plan rooms.
 */

export const PURCHASE_ORDER_ITEM_TYPE_KEY = 'PURCHASE_ORDER_ITEM';
export const FLOOR_PLAN_ITEM_DRAG_MIME = 'application/x-appdeck-floor-plan-item';

const PO_ITEM_DATA_KEY = 'data_purchase_order_item';

/**
 * readPoItemField
 *
 * @param {object|null|undefined} item
 * @param {string} fieldName
 * @returns {unknown}
 */
function readPoItemField(item, fieldName) {
  const section = item?.[PO_ITEM_DATA_KEY];
  if (section && typeof section === 'object' && fieldName in section) {
    return section[fieldName];
  }
  // Fallback: typed section keyed by type name variants
  const alt = item?.data_PURCHASE_ORDER_ITEM || item?.data_Purchase_Order_Item;
  if (alt && typeof alt === 'object' && fieldName in alt) {
    return alt[fieldName];
  }
  return undefined;
}

/**
 * readItemRoomKey
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function readItemRoomKey(item) {
  return String(readPoItemField(item, 'roomKey') || '').trim();
}

/**
 * readItemRoomName
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function readItemRoomName(item) {
  return String(readPoItemField(item, 'room') || '').trim();
}

/**
 * readItemFloorName
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function readItemFloorName(item) {
  return String(readPoItemField(item, 'floor') || '').trim();
}

/**
 * resolveFloorNameForRoom
 *
 * Looks up the layout floor name that owns a room id.
 *
 * @param {object|null|undefined} layout
 * @param {string|null|undefined} roomId
 * @returns {string}
 */
export function resolveFloorNameForRoom(layout, roomId) {
  const id = String(roomId || '').trim();
  if (!id) return '';
  const floors = Array.isArray(layout?.floors) ? layout.floors : [];
  for (let i = 0; i < floors.length; i += 1) {
    const floor = floors[i];
    const rooms = Array.isArray(floor?.rooms) ? floor.rooms : [];
    if (rooms.some((room) => String(room?.id || '').trim() === id)) {
      return String(floor?.name || '').trim();
    }
  }
  return '';
}

/**
 * listRoomIdsOnFloor
 *
 * @param {object|null|undefined} layout
 * @param {string|null|undefined} floorId
 * @returns {string[]}
 */
export function listRoomIdsOnFloor(layout, floorId) {
  const id = String(floorId || '').trim();
  if (!id) return [];
  const floors = Array.isArray(layout?.floors) ? layout.floors : [];
  const floor = floors.find((row) => String(row?.id || '').trim() === id) || null;
  const rooms = Array.isArray(floor?.rooms) ? floor.rooms : [];
  return rooms
    .map((room) => String(room?.id || '').trim())
    .filter(Boolean);
}

/**
 * applyPlacementOverride
 *
 * @param {object} item
 * @param {{ roomKey?: string, room?: string, floor?: string }|null|undefined} override
 * @returns {object}
 */
export function applyPlacementOverride(item, override) {
  if (!item || !override) return item;
  const section = {
    ...(item?.[PO_ITEM_DATA_KEY] || {}),
    roomKey: override.roomKey ?? '',
    room: override.room ?? '',
    floor: override.floor ?? '',
  };
  return {
    ...item,
    [PO_ITEM_DATA_KEY]: section,
  };
}

/**
 * buildPlacementPatch
 *
 * @param {object} item
 * @param {{ roomKey?: string, room?: string, floor?: string }} placement
 * @returns {object}
 */
export function buildPlacementPatch(item, placement = {}) {
  const section = {
    ...(item?.[PO_ITEM_DATA_KEY] || {}),
    roomKey: String(placement.roomKey || '').trim(),
    room: String(placement.room || '').trim(),
    floor: String(placement.floor || '').trim(),
  };
  return { [PO_ITEM_DATA_KEY]: section };
}

/**
 * groupFloorPlanItems
 *
 * @param {object[]} items
 * @param {object|null|undefined} layout
 * @returns {{ unassigned: object[], groups: { roomKey: string, roomName: string, items: object[] }[] }}
 */
export function groupFloorPlanItems(items, layout) {
  const roomNameById = new Map();
  const floors = Array.isArray(layout?.floors) ? layout.floors : [];
  floors.forEach((floor) => {
    (floor.rooms || []).forEach((room) => {
      if (room?.id) roomNameById.set(room.id, room.name || 'Room');
    });
  });

  const unassigned = [];
  const byRoom = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const roomKey = readItemRoomKey(item);
    if (!roomKey || !roomNameById.has(roomKey)) {
      unassigned.push(item);
      return;
    }
    if (!byRoom.has(roomKey)) {
      byRoom.set(roomKey, []);
    }
    byRoom.get(roomKey).push(item);
  });

  const groups = Array.from(byRoom.entries()).map(([roomKey, roomItems]) => ({
    roomKey,
    roomName: roomNameById.get(roomKey) || readItemRoomName(roomItems[0]) || 'Room',
    items: roomItems,
  }));

  groups.sort((a, b) => a.roomName.localeCompare(b.roomName));
  unassigned.sort((a, b) => {
    const left = String(a?.title || '').trim();
    const right = String(b?.title || '').trim();
    return left.localeCompare(right);
  });

  return { unassigned, groups };
}

/**
 * countItemsByRoomKey
 *
 * @param {object[]} items
 * @returns {Map<string, number>}
 */
export function countItemsByRoomKey(items) {
  const counts = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const roomKey = readItemRoomKey(item);
    if (!roomKey) return;
    counts.set(roomKey, (counts.get(roomKey) || 0) + 1);
  });
  return counts;
}
